import {
   NavigationGuardNext,
   RouteLocationNormalized,
   RouteLocationRaw,
} from "vue-router"
import {
   getAuthData,
   getOrg,
   hasActiveStripeSubscription,
   hasStripePriceId,
   getUser,
   getNumberOfOrgs,
   hasActiveSubscription,
   hasInactiveSubscription,
} from "@/services/clerk"
import { addBreadcrumb } from "@/services/sentry"
import { useProjectStore } from "@stores/projectStore"
import { createStripeCheckoutSession } from "@/services/adminBackendClient"
import { useOnboardingStore } from "@stores/onboardingStore"
import { collectError } from "@utils/errors"

// Guard result types
export type GuardResult = {
   continue: boolean
   redirectTo?: RouteLocationRaw
   error?: Error
}

export type GuardFunction = (
   to: RouteLocationNormalized,
   from: RouteLocationNormalized,
) => Promise<GuardResult>

// Higher-order function to compose multiple guards
export const composeGuards = (...guards: GuardFunction[]) => {
   return async (
      to: RouteLocationNormalized,
      from: RouteLocationNormalized,
      next: NavigationGuardNext,
   ) => {
      addBreadcrumb({
         category: "Route Guard",
         message: `Running composed guards for route ${to.fullPath}`,
         level: "info",
      })

      // Execute each guard in sequence
      for (const guard of guards) {
         const result = await guard(to, from)
         if (!result.continue) {
            if (result.error) {
               console.error(result.error)
            }

            if (result.redirectTo) {
               return next(result.redirectTo)
            }

            return next(false)
         }
      }

      return next()
   }
}

// Basic guard for validating user is logged in
export const requireLogin: GuardFunction = async (to, _from) => {
   addBreadcrumb({
      category: "Route Guard",
      message: `Validating user is logged in for route ${to.fullPath}`,
      level: "info",
   })

   const { sessionActive } = await getAuthData()

   if (!sessionActive) {
      return {
         continue: false,
         redirectTo: { name: "SignIn" },
      }
   }

   return { continue: true }
}

// Guard for validating active organization
export const requireActiveOrg: GuardFunction = async (to, _from) => {
   addBreadcrumb({
      category: "Route Guard",
      message: `Validating active organization for route ${to.fullPath}`,
      level: "info",
   })

   const { decodedToken } = await getAuthData()

   if (!decodedToken || !decodedToken.org_id) {
      return {
         continue: false,
         redirectTo: { name: "SelectOrg" },
      }
   }

   return { continue: true }
}

// Guard for validating stripe subscription
export const requireActiveSubscription: GuardFunction = async (to, _from) => {
   addBreadcrumb({
      category: "Route Guard",
      message: `Validating active subscription for route ${to.fullPath}`,
      level: "info",
   })

   const hasSubscription = await hasActiveStripeSubscription()

   if (!hasSubscription) {
      // If no active subscription, check if the user has a stripePriceId
      const hasPriceId = await hasStripePriceId()

      // If they have a stripePriceId, redirect to Stripe checkout
      if (hasPriceId) {
         try {
            // Get checkout session URL from the backend
            const response = await createStripeCheckoutSession()

            // Redirect to Stripe checkout
            window.location.href = response.data.url

            // Return false to prevent further navigation
            return { continue: false }
         } catch (error) {
            collectError(error as Error)
            // If there's an error, fall back to subscription error page
            return {
               continue: false,
               redirectTo: { name: "SubscriptionError" },
            }
         }
      }

      const inactiveSub = await hasInactiveSubscription()

      if (inactiveSub) {
         return {
            continue: false,
            redirectTo: { name: "SubscriptionError" },
         }
      }

      // No stripePriceId or no active subscription - redirect to select subscription
      return {
         continue: false,
         redirectTo: { name: "SelectSubscription" },
      }
   }

   return { continue: true }
}

// Guard for validating a user has a stripe price ID
export const requireStripePriceId: GuardFunction = async (to, _from) => {
   addBreadcrumb({
      category: "Route Guard",
      message: `Validating user has stripe price ID for route ${to.fullPath}`,
      level: "info",
   })

   const hasPrice = await hasStripePriceId()

   if (!hasPrice) {
      return {
         continue: false,
         redirectTo: { name: "SubscriptionError" },
      }
   }

   return { continue: true }
}

// Guard for validating employee status
export const requireEzbotEmployee: GuardFunction = async (to, _from) => {
   addBreadcrumb({
      category: "Route Guard",
      message: `Validating user is Ezbot employee for route ${to.fullPath}`,
      level: "info",
   })

   // In development mode, treat all users as employees if the flag is set
   if (import.meta.env.VITE_MOCK_EMPLOYEE === "true") {
      console.log(
         "Development mode: Treating user as Ezbot employee in route guard",
      )
      return { continue: true }
   }

   const { decodedToken } = await getAuthData()

   if (!decodedToken || decodedToken.user_metadata?.isEzbotEmployee !== true) {
      return {
         continue: false,
         redirectTo: { name: "Home" },
      }
   }

   return { continue: true }
}

// Guard for validating organization slug matches current organization
export const validateOrgSlug: GuardFunction = async (to, _from) => {
   const routeOrgSlug = to.params.orgSlug as string

   if (!routeOrgSlug) {
      return { continue: true }
   }

   const org = await getOrg()

   if (!org) {
      return {
         continue: false,
         redirectTo: { name: "SelectOrg" },
      }
   }

   if (org.slug !== routeOrgSlug) {
      return {
         continue: false,
         redirectTo: {
            name: to.name as string,
            params: { ...to.params, orgSlug: org.slug },
         },
      }
   }

   return { continue: true }
}

// Guard for validating project ID
export const requireValidProject: GuardFunction = async (to, _from) => {
   addBreadcrumb({
      category: "Route Guard",
      message: `Validating project for route ${to.fullPath}`,
      level: "info",
   })

   const projectStore = useProjectStore()
   const projectId = to.params.projectId ? Number(to.params.projectId) : null

   // If no projectId in route, handle redirection
   if (!projectId) {
      const org = await getOrg()
      if (!org) {
         return {
            continue: false,
            redirectTo: { name: "SelectOrg" },
         }
      }

      const { slug } = org

      // If we have a projectId in the store, use that
      if (projectStore.projectId) {
         return {
            continue: false,
            redirectTo: {
               name: "ProjectHome",
               params: {
                  projectId: projectStore.projectId,
                  orgSlug: slug,
               },
            },
         }
      }

      // If we have projects in the store, use the first one
      if (projectStore.projects.length > 0) {
         return {
            continue: false,
            redirectTo: {
               name: "ProjectHome",
               params: {
                  projectId: projectStore.projects[0].id,
                  orgSlug: slug,
               },
            },
         }
      }

      // If we don't have projects in the store, fetch them
      try {
         await projectStore.getActiveOrgProjects()
         const projects = projectStore.projects

         if (!projects || projects.length === 0) {
            return {
               continue: false,
               redirectTo: { name: "CreateProject", params: { orgSlug: slug } },
            }
         }

         return {
            continue: false,
            redirectTo: {
               name: "ProjectHome",
               params: { projectId: projects[0].id, orgSlug: slug },
            },
         }
      } catch (error) {
         return {
            continue: false,
            redirectTo: { name: "OrgHome", params: { orgSlug: slug } },
            error: error as Error,
         }
      }
   }

   // Set the current projectId in the store
   projectStore.projectId = projectId

   // If we already have projects in the store, check if the current project exists
   if (projectStore.projects.length > 0) {
      const projectExists = projectStore.projects.some(
         (p) => p.id === projectId,
      )

      if (projectExists) {
         return { continue: true }
      }
   }

   // If we don't have projects or the current project doesn't exist in our list,
   // fetch projects from the backend (this will also validate if the user has access)
   try {
      await projectStore.getActiveOrgProjects()
      const projectExists = projectStore.projects.some(
         (p) => p.id === projectId,
      )

      if (!projectExists) {
         // Project doesn't exist or user doesn't have access
         const org = await getOrg()
         if (!org) {
            return {
               continue: false,
               redirectTo: { name: "SelectOrg" },
            }
         }

         const { slug } = org

         if (projectStore.projects.length > 0) {
            return {
               continue: false,
               redirectTo: {
                  name: "ProjectHome",
                  params: {
                     projectId: projectStore.projects[0].id,
                     orgSlug: slug,
                  },
               },
            }
         } else {
            return {
               continue: false,
               redirectTo: { name: "CreateProject", params: { orgSlug: slug } },
            }
         }
      }

      return { continue: true }
   } catch (error) {
      // Handle API errors (e.g., unauthorized)
      const org = await getOrg()
      if (!org) {
         return {
            continue: false,
            redirectTo: { name: "SelectOrg" },
         }
      }

      const { slug } = org

      return {
         continue: false,
         redirectTo: { name: "OrgHome", params: { orgSlug: slug } },
         error: error as Error,
      }
   }
}

export const requireOnboarded: GuardFunction = async (to, _from) => {
   addBreadcrumb({
      category: "Route Guard",
      message: `Validating onboarding data for route ${to.fullPath}`,
      level: "info",
   })

   const onboardingStore = useOnboardingStore()
   await onboardingStore.fetchProjectOnboarding()
   const { dataReceived, rewardReceived } = onboardingStore
   if (!dataReceived || !rewardReceived) {
      return {
         continue: false,
         redirectTo: {
            name: "OnboardingHome",
            params: {
               orgSlug: to.params.orgSlug as string,
               projectId: to.params.projectId as string,
            },
         },
      }
   }

   return { continue: true }
}

export const putOnOnboardingStep = async (
   to: RouteLocationNormalized,
   from: RouteLocationNormalized,
   next: NavigationGuardNext,
) => {
   addBreadcrumb({
      category: "Route Guard",
      message: `Putting user on appropriate onboarding step for route ${to.fullPath}`,
      level: "info",
   })
   // If not logged in, proceed to Sign in / Sign up
   const user = await getUser()
   if (!user) {
      // If already on a sign-in/sign-up route, allow them to continue
      if (
         to.name === "SignIn" ||
         to.name === "SignUp" ||
         to.name?.toString().startsWith("SignIn") ||
         to.name === "VerifyEmailAddress"
      ) {
         return next()
      }
      // Otherwise redirect to sign in
      return next({ name: "SignIn" })
   }

   // If hasn't created an org, proceed to create org
   const numOrgs = await getNumberOfOrgs()
   if (numOrgs === 0) {
      return next({ name: "OnboardingCreateOrg" })
   }

   const isActive = await hasActiveSubscription()

   // If the user has an inactive subscription and we know the user selected tier, proceed to checkout
   const { tier } = useOnboardingStore()
   if (!isActive && (tier === "starter_tier" || tier === "professional_tier")) {
      return next({ name: "OrgsSetupCheckout" })
   }

   // If the user has an inactive subscription and we don't know the user selected tier, proceed to select subscription
   if (!isActive) {
      return next({ name: "SelectSubscription" })
   }

   // If the user has an active subscription and no projects, proceed to create project
   const projectStore = useProjectStore()
   if (isActive && projectStore.projects.length === 0) {
      return next({ name: "CreateProject" })
   }

   // If the user has an active subscription and a project, proceed to project home
   if (isActive) {
      const projectStore = useProjectStore()
      const org = await getOrg()
      return next({
         name: "ProjectHome",
         params: { orgSlug: org!.slug, projectId: projectStore.projectId },
      })
   }

   return next()
}

// Common guard combinations
export const authGuard = composeGuards(requireLogin)
export const orgGuard = composeGuards(
   requireLogin,
   requireActiveOrg,
   validateOrgSlug,
)
export const subscriptionGuard = composeGuards(
   requireLogin,
   requireActiveOrg,
   requireActiveSubscription,
)
export const projectGuard = composeGuards(
   requireLogin,
   requireActiveOrg,
   validateOrgSlug,
   requireValidProject,
)
export const subscriptionProjectGuard = composeGuards(
   requireLogin,
   requireActiveOrg,
   requireActiveSubscription,
   validateOrgSlug,
   requireValidProject,
)
export const employeeGuard = composeGuards(requireLogin, requireEzbotEmployee)
export const createOrgGuard = composeGuards(requireLogin)
export const onboardedGuard = composeGuards(
   requireLogin,
   requireActiveSubscription,
   validateOrgSlug,
   requireValidProject,
   requireOnboarded,
)
